import { useMemo } from "react";
import { RefinanceContext } from "../types";
import { splitLeaBankDebts } from "../utils/lea-bank-debts";
import {
	hasNothingToRefinance,
	resolveRefinanceAllocation,
} from "../utils/refinance-allocation";
import { hasReportedBalance } from "../utils/seed-debts-from-context";

export type RefinanceScenario = "forced" | "voluntary" | "skippable";

export interface RefinanceScenarioInfo {
	scenario: RefinanceScenario;
	isForced: boolean;
	isVoluntary: boolean;
	/** Nothing required, nothing applied for and nothing found: no bar to show. */
	isSkippable: boolean;
	/** The redemption the customer has to cover with debts of their own. */
	requiredRedemption: number;
	/** The existing Lea Bank loan a top-up settles, which asks nothing of them. */
	existingLoan: number;
	isTopUp: boolean;
}

export const useRefinanceScenario = (
	context: RefinanceContext
): RefinanceScenarioInfo =>
	useMemo(() => {
		const { offer, appliedRefinance, debts } = context;
		const { internal, mandatory } = resolveRefinanceAllocation({
			offer,
			appliedRefinance,
			collected: 0,
		});

		const isSkippable = hasNothingToRefinance({
			offer,
			appliedRefinance,
			ucDebtCount:
				splitLeaBankDebts(debts).others.filter(hasReportedBalance).length,
		});

		const isForced = mandatory.capacity > 0;
		const isVoluntary = !isForced && !isSkippable;

		const scenario: RefinanceScenario = isForced
			? "forced"
			: isVoluntary
				? "voluntary"
				: "skippable";

		return {
			scenario,
			isForced,
			isVoluntary,
			isSkippable,
			requiredRedemption: mandatory.capacity,
			existingLoan: internal,
			isTopUp: internal > 0,
		};
	}, [context]);
